/**
 * URL Extractor
 *
 * Extracts and normalizes URLs from free-form text with optional SNS exclusion.
 */

import { Patterns } from "../../constants/patterns";

interface DetectUrlOptions {
    excludeSNS?: boolean;
}

const urlPattern = Patterns.reset(Patterns.URL);
const snsExcludePatterns: RegExp[] = Patterns.OGP_EXCLUDED_SNS_PLATFORMS;

function cleanUrl(rawUrl: string): string {
    let url = Patterns.decodeAmpersand(rawUrl).trim();

    while (/::[A-Za-z0-9_-]{2,}::$/.test(url)) {
        url = url.replace(/::[A-Za-z0-9_-]{2,}::$/, "");
    }

    url = url.replace(/[\s).,;!]+$/, "");

    const pathMatch = url.match(Patterns.PATH_ID);
    if (pathMatch) {
        return pathMatch[1];
    }

    const ytMatch = url.match(Patterns.YOUTUBE_TRAILING);
    if (ytMatch) {
        return ytMatch[1];
    }

    return url;
}

function extractAnchorUrls(content: string): string[] {
    const anchorPattern = Patterns.reset(Patterns.ANCHOR_HREF);
    const anchors: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = anchorPattern.exec(content)) !== null) {
        anchors.push(cleanUrl(match[1]));
    }

    return anchors;
}

function extractPlainUrls(content: string): string[] {
    const normalizedContent = content.replace(Patterns.URL_PROTOCOL_PREFIX, " $1");
    const matches = Patterns.match(normalizedContent, Patterns.URL) || [];
    return matches.map((url) => cleanUrl(url));
}

function validateAndDedupe(urls: string[], { excludeSNS = true }: DetectUrlOptions = {}): string[] {
    const unique = [...new Set(urls)];

    return unique.filter((url) => {
        try {
            new URL(url);
            if (excludeSNS && snsExcludePatterns.some((pattern) => pattern.test(url))) {
                return false;
            }
            return true;
        } catch {
            return false;
        }
    });
}

function detectUrls(content: string, { excludeSNS = true }: DetectUrlOptions = {}): string[] {
    if (!content) return [];

    const anchorUrls = extractAnchorUrls(content);
    if (anchorUrls.length > 0) {
        return validateAndDedupe(anchorUrls, { excludeSNS });
    }

    const plainUrls = extractPlainUrls(content);
    return validateAndDedupe(plainUrls, { excludeSNS });
}

const UrlExtractor = {
    detectUrls,
    cleanUrl,
    urlPattern,
};

export { UrlExtractor, cleanUrl, detectUrls, urlPattern };
